import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { InvoiceMascot, MascotExpression } from './invoice-mascot.component';
import './invoice-tour.style.scss';

export interface TourStep {
  /** CSS selector (or ordered list — first present wins) of the element to
   *  spotlight. Omit (or placement 'center') for a centered card. */
  anchor?: string | string[];
  title: string;
  body: React.ReactNode;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  /** px padding around the spotlighted element */
  padding?: number;
  /** When false, the Next button is disabled (strict gating). */
  gate?: boolean;
  /** DOM-based gate: Next stays disabled until an element matching this selector
   *  exists (re-checked live). Use for child state we can't read directly. */
  gateSelector?: string;
  /** Hint shown under the body while the step is gated. */
  gateHint?: string;
  /** Hide the Next button (e.g. the step auto-advances on a user action). */
  hideNext?: boolean;
  /** When anchor is a list, spotlight the UNION of all present elements
   *  (e.g. a dropdown trigger + its open options list). Default: first-present. */
  union?: boolean;
  /** Extra selectors unioned INTO the spotlight only when present (added to the
   *  first-present anchor). Use for an optional open sub-element like a dropdown
   *  menu, without dragging a far-away fallback anchor into the union. */
  unionWith?: string[];
  /** Skip this step entirely if its anchor isn't in the DOM (e.g. a section that
   *  is conditionally hidden). Handled by the controller when navigating. */
  skipIfAbsent?: boolean;
  /** Celebratory finish step — renders a confetti burst behind the card. */
  celebrate?: boolean;
}

// Confetti pieces for the celebration step (fixed set → deterministic render).
const CONFETTI = Array.from({ length: 70 }, (_, i) => {
  const colors = ['#7c3aed', '#a855f7', '#c084fc', '#f0abfc', '#facc15', '#34d399', '#60a5fa'];
  const seed = (i * 9301 + 49297) % 233280;
  const rnd = seed / 233280;
  return {
    left: (i / 44) * 100 + (rnd * 4 - 2),
    color: colors[i % colors.length],
    delay: rnd * 1.2,
    duration: 2.4 + rnd * 1.8,
    size: 6 + Math.round(rnd * 6),
    round: i % 3 === 0,
  };
});

interface InvoiceTourProps {
  run: boolean;
  stepIndex: number;
  steps: TourStep[];
  /** Whether a reachable (present) previous step exists. When false, the Back
   *  button is hidden — e.g. on the first visible step when an earlier step is
   *  hidden (the Workflow step on mobile). Defaults to true. */
  canPrev?: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
}

interface Box { top: number; left: number; width: number; height: number; }

export const InvoiceTour: React.FC<InvoiceTourProps> = ({ run, stepIndex, steps, canPrev = true, onPrev, onNext, onClose }) => {
  const { t } = useTranslation();
  const step = steps[stepIndex];
  const [box, setBox] = useState<Box | null>(null);
  // Mobile: let the user minimise the hint (so it doesn't cover form fields).
  const [collapsed, setCollapsed] = useState(false);
  // Guide mascot in the popover header — reacts to hovering the action buttons.
  const [guideMood, setGuideMood] = useState<MascotExpression>('idle');
  const guideRef = useRef<HTMLSpanElement | null>(null);
  const guideEyesRef = useRef<SVGGElement | null>(null);
  useEffect(() => { setCollapsed(false); setGuideMood('idle'); }, [stepIndex]);

  // Guide's eyes follow the cursor while it's over the popover (ref-only).
  const trackGuideEyes = (e: React.MouseEvent) => {
    const g = guideRef.current?.getBoundingClientRect();
    if (!g || !guideEyesRef.current) return;
    const ox = g.left + g.width / 2;
    const oy = g.top + g.height * 0.42;
    const dx = Math.max(-1, Math.min(1, (e.clientX - ox) / 200));
    const dy = Math.max(-1, Math.min(1, (e.clientY - oy) / 200));
    guideEyesRef.current.style.transform = `translate(${dx * 2.6}px, ${dy * 2.2}px)`;
  };
  const resetGuideEyes = () => {
    if (guideEyesRef.current) guideEyesRef.current.style.transform = 'translate(0px, 0px)';
  };

  // Resolve the target element — supports an ordered list of selectors
  // (first one present in the DOM wins, e.g. prefer an open sub-modal).
  const resolveEl = useCallback((): HTMLElement | null => {
    if (!step?.anchor) return null;
    const list = Array.isArray(step.anchor) ? step.anchor : [step.anchor];
    for (const sel of list) {
      const el = document.querySelector(sel) as HTMLElement | null;
      if (el) return el;
    }
    return null;
  }, [step]);

  // First anchor that is actually VISIBLE (non-zero size). Used for scrolling —
  // an in-DOM-but-display:none element (e.g. the desktop-only table header on
  // mobile) can't be scrolled into view, so we skip it and scroll the real one.
  const resolveVisibleEl = useCallback((): HTMLElement | null => {
    if (!step?.anchor) return null;
    const list = Array.isArray(step.anchor) ? step.anchor : [step.anchor];
    for (const sel of list) {
      const el = document.querySelector(sel) as HTMLElement | null;
      if (el && (el.offsetWidth > 0 || el.offsetHeight > 0)) return el;
    }
    return null;
  }, [step]);

  const measure = useCallback(() => {
    if (!run || !step) return;
    if (step.placement === 'center' || !step.anchor) { setBox(null); return; }
    const pad = step.padding ?? 8;
    const list = Array.isArray(step.anchor) ? step.anchor : [step.anchor];

    if (step.union) {
      // Union of every present selector's rect (e.g. trigger + open list).
      // Skip hidden elements (display:none → 0×0 rect at 0,0) so they don't drag
      // the spotlight to the top-left corner (e.g. desktop-only table header on mobile).
      const rects = list
        .map((s) => document.querySelector(s) as HTMLElement | null)
        .filter(Boolean)
        .map((el) => (el as HTMLElement).getBoundingClientRect())
        .filter((r) => r.width > 0 && r.height > 0);
      if (!rects.length) { setBox(null); return; }
      const top = Math.min(...rects.map((r) => r.top));
      const left = Math.min(...rects.map((r) => r.left));
      const right = Math.max(...rects.map((r) => r.right));
      const bottom = Math.max(...rects.map((r) => r.bottom));
      setBox({ top: top - pad, left: left - pad, width: right - left + pad * 2, height: bottom - top + pad * 2 });
      return;
    }

    const el = resolveEl();
    if (!el) { setBox(null); return; }
    // Primary rect + any present `unionWith` sub-elements (e.g. an open dropdown
    // menu). Only present, non-zero rects count.
    const rects = [el.getBoundingClientRect()];
    if (step.unionWith) {
      step.unionWith.forEach((s) => {
        const e2 = document.querySelector(s) as HTMLElement | null;
        if (e2) rects.push(e2.getBoundingClientRect());
      });
    }
    const valid = rects.filter((r) => r.width > 0 && r.height > 0);
    if (!valid.length) { setBox(null); return; } // hidden/empty element
    const top = Math.min(...valid.map((r) => r.top));
    const left = Math.min(...valid.map((r) => r.left));
    const right = Math.max(...valid.map((r) => r.right));
    const bottom = Math.max(...valid.map((r) => r.bottom));
    setBox({ top: top - pad, left: left - pad, width: right - left + pad * 2, height: bottom - top + pad * 2 });
  }, [run, step, resolveEl]);

  useLayoutEffect(() => { measure(); }, [measure, stepIndex]);

  // While the tour runs, flag the <body> so CSS can neutralise the footer
  // action buttons (Preview / Save draft / Send) — they work again after.
  useEffect(() => {
    document.body.classList.toggle('iv-tour-active', run);
    return () => document.body.classList.remove('iv-tour-active');
  }, [run]);

  useEffect(() => {
    if (!run) return;
    // Center the target in the viewport once it's present. Some targets mount a
    // moment after the step opens (a revealed section / an opened modal), so we
    // keep retrying (via the interval + scroll handler) until it's found, then
    // stop. `block:'center'` scrolls every scrollable ancestor so the element
    // lands in the middle of the screen — the reliable "bring it in front".
    let scrolled = false;
    const tryScroll = () => {
      if (scrolled) return;
      if (!step?.anchor || step.placement === 'center') { scrolled = true; return; }
      const el = resolveVisibleEl();
      if (el) {
        scrolled = true;
        const mobileNow = window.innerWidth <= 640;
        if (mobileNow) {
          // A consolidated step often spotlights a whole card that's TALLER than
          // the phone screen. Centering it would hide its header; instead scroll
          // to its TOP (below the sticky header) so the heading being explained
          // is visible, with the instruction sheet sitting below. Shorter targets
          // still center into the upper-middle, clear of the bottom sheet.
          const rect = el.getBoundingClientRect();
          const tall = rect.height > window.innerHeight * 0.6;
          el.style.scrollMarginTop = '76px';
          el.style.scrollMarginBottom = `${Math.round(window.innerHeight * 0.32)}px`;
          el.scrollIntoView({ block: tall ? 'start' : 'center', inline: 'nearest', behavior: 'smooth' });
        } else {
          el.style.scrollMarginTop = '';
          el.style.scrollMarginBottom = '';
          el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
        }
      }
    };
    tryScroll();
    const on = () => { tryScroll(); measure(); };
    window.addEventListener('resize', on);
    window.addEventListener('scroll', on, true);
    // Retry scroll + re-measure periodically (target may mount slightly late).
    const id = window.setInterval(on, 300);
    return () => {
      window.removeEventListener('resize', on);
      window.removeEventListener('scroll', on, true);
      clearInterval(id);
    };
  }, [run, stepIndex, measure, step, resolveEl, resolveVisibleEl]);

  if (!run || !step) return null;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const POP_W = 340;
  const POP_H = 220;   // estimate used only for clamping
  const gap = 14;
  const m = 12;        // viewport margin

  // Popover position — clamped fully inside the viewport (robust for any anchor
  // size). 'top' placement anchors the card's BOTTOM edge above the target, so a
  // tall card never overlaps the target (fixes footer buttons near the bottom).
  const isMobile = vw <= 640;
  const clampX = (l: number) => Math.min(Math.max(l, m), vw - POP_W - m);
  let popStyle: React.CSSProperties;
  if (isMobile) {
    // Mobile: full-width sheet placed on whichever side actually CLEARS the
    // (centered) spotlight, with a capped height + internal scroll so it stays
    // compact and never hides the focused element. Centered when there's no anchor.
    const sheetMax = Math.round(vh * 0.42);
    const sheetBase: React.CSSProperties = { left: m, right: m, width: 'auto', maxHeight: `${sheetMax}px`, overflowY: 'auto' };
    if (!box) {
      popStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'auto', maxWidth: `calc(100vw - ${m * 2}px)` };
    } else {
      const bottomSheetTop = vh - sheetMax - m;   // y where a bottom sheet begins
      const topSheetBottom = sheetMax + m;         // y where a top sheet ends
      const fitsBottom = box.top + box.height + gap < bottomSheetTop;  // spotlight ends above a bottom sheet
      const fitsTop = box.top - gap > topSheetBottom;                  // spotlight starts below a top sheet
      // A card taller than the viewport is scrolled to its TOP (see the scroll
      // effect), so the sheet MUST go to the bottom — a top sheet would cover the
      // card heading we just brought into view.
      const isTall = box.height > vh - 2 * m;
      // Prefer the clearing side; if the target is huge and neither clears, drop
      // the sheet on the side with more free space.
      const useBottom = isTall || fitsBottom || (!fitsTop && (vh - box.bottom) >= box.top);
      popStyle = useBottom
        ? { ...sheetBase, bottom: m }
        : { ...sheetBase, top: m };
    }
  } else if (!box) {
    popStyle = { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  } else {
    // Auto-avoid overlap: use the step's preferred side if the popover fits fully
    // beside the spotlight there; otherwise drop it into the largest CLEAR margin
    // (e.g. the side gap next to a big centered modal). Falls back to the
    // preferred side (clamped) only when nothing fits — genuine full-screen box.
    const preferred = step.placement || 'bottom';
    const fits: Record<string, boolean> = {
      right: box.left + box.width + gap + POP_W + m <= vw,
      left: box.left - gap - POP_W - m >= 0,
      top: box.top - gap - POP_H - m >= 0,
      bottom: box.top + box.height + gap + POP_H + m <= vh,
    };
    const order = [preferred, 'right', 'left', 'bottom', 'top'].filter((v, i, a) => a.indexOf(v) === i);
    const side = (order.find((s) => fits[s]) || preferred) as 'top' | 'bottom' | 'left' | 'right';

    if (side === 'left' || side === 'right') {
      const left = side === 'right' ? box.left + box.width + gap : box.left - gap - POP_W;
      const top = Math.min(Math.max(box.top + box.height / 2 - POP_H / 2, m), vh - POP_H - m);
      popStyle = { top, left: clampX(left), width: POP_W, maxWidth: `calc(100vw - ${m * 2}px)` };
    } else if (side === 'top') {
      const left = clampX(box.left + box.width / 2 - POP_W / 2);
      const bottom = Math.min(Math.max(vh - (box.top - gap), m), vh - m);
      popStyle = { bottom, left, width: POP_W, maxWidth: `calc(100vw - ${m * 2}px)` };
    } else { // bottom
      const left = clampX(box.left + box.width / 2 - POP_W / 2);
      const top = Math.min(Math.max(box.top + box.height + gap, m), vh - POP_H - m);
      popStyle = { top, left, width: POP_W, maxWidth: `calc(100vw - ${m * 2}px)` };
    }
  }

  const isLast = stepIndex === steps.length - 1;
  // Re-evaluated on every render; the periodic measure() keeps this fresh so a
  // DOM-based gate (e.g. a toggle turning on) enables Next within a moment.
  const gated = step.gate === false || (!!step.gateSelector && !document.querySelector(step.gateSelector));

  // Click-blocker clip-path: a full-screen shape with a rectangular hole over
  // the spotlight, so only the highlighted element (and popover) stay clickable.
  const blockerClip = box
    ? `polygon(0 0, ${vw}px 0, ${vw}px ${vh}px, 0 ${vh}px, 0 0, ${box.left}px ${box.top}px, ${box.left}px ${box.top + box.height}px, ${box.left + box.width}px ${box.top + box.height}px, ${box.left + box.width}px ${box.top}px, ${box.left}px ${box.top}px)`
    : undefined;

  return createPortal(
    <div className="iv-tour" role="dialog" aria-modal="true">
      {/* Blocks clicks on the dimmed page (hole over the spotlight lets the
          highlighted element through) */}
      <div className="iv-tour-blocker" style={blockerClip ? { clipPath: blockerClip, WebkitClipPath: blockerClip } : undefined} />

      {/* Dim + spotlight */}
      {box ? (
        <div className="iv-tour-spot" style={{ top: box.top, left: box.left, width: box.width, height: box.height }} />
      ) : (
        <div className={`iv-tour-dim${step.celebrate ? ' iv-tour-dim--celebrate' : ''}`} />
      )}

      {/* Celebration confetti */}
      {step.celebrate && (
        <div className="iv-confetti" aria-hidden="true">
          {CONFETTI.map((c, i) => (
            <span
              key={i}
              className={`iv-confetti-piece${c.round ? ' is-round' : ''}`}
              style={{
                left: `${c.left}%`,
                width: `${c.size}px`,
                height: `${c.size + (c.round ? 0 : 4)}px`,
                background: c.color,
                animationDelay: `${c.delay}s`,
                animationDuration: `${c.duration}s`,
              }}
            />
          ))}
        </div>
      )}

      {/* Collapsed → small floating bubble (mobile): keeps fields usable */}
      {collapsed ? (
        <button type="button" className="iv-tour-bubble" onClick={() => setCollapsed(false)} aria-label="Show tour hint">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 16v-4M12 8h.01" /></svg>
          <span>{stepIndex + 1}/{steps.length}</span>
        </button>
      ) : (
      /* Popover card */
      <div
        className={`iv-tour-pop${step.celebrate ? ' iv-tour-pop--celebrate' : ''}`}
        style={popStyle}
        onMouseMove={trackGuideEyes}
        onMouseLeave={resetGuideEyes}
      >
        <div className="iv-tour-pop__head">
          <div className="iv-tour-head-left">
            {/* Same guide character on every step — stands in front of the step number */}
            {!step.celebrate && (
              <span className="iv-tour-guide" key={stepIndex} ref={guideRef}>
                <InvoiceMascot expression={guideMood} eyesRef={guideEyesRef} />
              </span>
            )}
            <span className="iv-tour-count">{stepIndex + 1} / {steps.length}</span>
          </div>
          <div className="iv-tour-head-btns">
            {!step.celebrate && (
              <button type="button" className="iv-tour-min" onClick={() => setCollapsed(true)} aria-label="Minimise hint">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14" /></svg>
              </button>
            )}
            <button
              type="button"
              className="iv-tour-x"
              onClick={onClose}
              aria-label="Close tour"
              onMouseEnter={() => setGuideMood('sad')}
              onMouseLeave={() => setGuideMood('idle')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12" /></svg>
            </button>
          </div>
        </div>
        {step.celebrate && (
          <div className="iv-celebrate-guide" aria-hidden="true">
            <span className="iv-celebrate-ring" />
            <span className="iv-celebrate-ring iv-celebrate-ring--2" />
            <span className="iv-celebrate-bubble">{t('IVTOUR_CELEBRATE_LINE1')}<br />{t('IVTOUR_CELEBRATE_LINE2')}</span>
            <span className="iv-celebrate-bot">
              <InvoiceMascot expression="happy" />
            </span>
          </div>
        )}
        <h4 className="iv-tour-title">{step.title}</h4>
        <div className="iv-tour-body">{step.body}</div>
        {gated && step.gateHint && <div className="iv-tour-hint">{step.gateHint}</div>}
        <div className="iv-tour-actions">
          <button
            type="button"
            className="iv-tour-skip"
            onClick={onClose}
            onMouseEnter={() => setGuideMood('sad')}
            onMouseLeave={() => setGuideMood('idle')}
          >{t('IVTOUR_SKIP_BTN')}</button>
          <div className="iv-tour-actions__right">
            {stepIndex > 0 && canPrev && (
              <button
                type="button"
                className="iv-tour-back"
                onClick={onPrev}
                onMouseEnter={() => setGuideMood('confused')}
                onMouseLeave={() => setGuideMood('idle')}
              >{t('IVTOUR_BACK_BTN')}</button>
            )}
            {!step.hideNext && (
              <button
                type="button"
                className="iv-tour-next"
                disabled={gated}
                onClick={onNext}
                onMouseEnter={() => setGuideMood('happy')}
                onMouseLeave={() => setGuideMood('idle')}
              >
                {isLast ? t('IVTOUR_FINISH_BTN') : t('IVTOUR_NEXT_BTN')}
              </button>
            )}
          </div>
        </div>
      </div>
      )}
    </div>,
    document.body
  );
};

export default InvoiceTour;
